import { useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Check, Clock, Target as TargetIcon, TrendingUp } from "lucide-react";
import TimezonePicker from "./TimezonePicker";
import FinPipMark from "./FinPipMark";
import { onboardingApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

/**
 * First-run setup wizard, shown once right after registration (gated by the
 * `needs_onboarding` flag from /api/me). Collects the user's display timezone,
 * their broker/MT5 timezone, their trading target and the % growth per level,
 * then POSTs /api/onboarding and refreshes auth so the app unlocks.
 */

function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Dubai";
  } catch {
    return "Asia/Dubai";
  }
}

const STEPS = [
  { key: "you", labelKey: "setup.step.you", Icon: Clock },
  { key: "broker", labelKey: "setup.step.broker", Icon: Clock },
  { key: "target", labelKey: "setup.step.target", Icon: TargetIcon },
  { key: "levels", labelKey: "setup.step.levels", Icon: TrendingUp },
] as const;

export default function SetupWizard() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [displayTz, setDisplayTz] = useState(browserTz());
  const [brokerTz, setBrokerTz] = useState("Europe/Istanbul");
  const [startBalance, setStartBalance] = useState<string>("");
  const [goalAmount, setGoalAmount] = useState<string>("");
  const [levelPct, setLevelPct] = useState<string>("1");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const goal = parseFloat(goalAmount);
  const pct = parseFloat(levelPct);
  const start = startBalance.trim() === "" ? null : parseFloat(startBalance);

  const canNext = useMemo(() => {
    if (step === 0) return Boolean(displayTz);
    if (step === 1) return Boolean(brokerTz);
    if (step === 2) return Number.isFinite(goal) && goal > 0 && (start === null || (Number.isFinite(start) && start >= 0));
    if (step === 3) return Number.isFinite(pct) && pct > 0 && pct <= 100;
    return true;
  }, [step, displayTz, brokerTz, goal, start, pct]);

  const isLast = step === STEPS.length - 1;

  async function finish() {
    setSubmitting(true);
    setError(null);
    try {
      await onboardingApi.complete({
        display_timezone: displayTz,
        mt5_import_timezone: brokerTz,
        goal_amount: goal,
        start_balance: start ?? undefined,
        level_step_pct: pct,
      });
      await refresh();
    } catch (err) {
      setError((err as Error).message || t("setup.error"));
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <FinPipMark className="w-10 h-10 text-[#0c34f4] dark:text-white" />
          <div>
            <p className="text-xl font-bold text-parchment tracking-tight">{user?.name ? t("setup.welcomeName", { name: user.name }) : t("setup.welcome")}</p>
            <p className="text-parchment-muted text-xs">{t("setup.subtitle")}</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1">
              <div
                className="h-1.5 rounded-full transition-colors"
                style={{
                  backgroundColor: i <= step ? "#d7b13c" : "rgb(var(--border-soft))",
                }}
              />
            </div>
          ))}
        </div>

        <section className="glass-gold p-6 sm:p-8">
          <div className="mb-5 flex items-center gap-2 text-parchment">
            {(() => {
              const Icon = STEPS[step].Icon;
              return <Icon size={18} className="text-gold-400" />;
            })()}
            <h1 className="text-lg font-semibold">
              {t("setup.stepOf", { current: step + 1, total: STEPS.length })} · {t(STEPS[step].labelKey)}
            </h1>
          </div>

          {step === 0 && (
            <div className="grid gap-3">
              <p className="text-sm text-parchment-muted">
                {t("setup.you.desc")}
              </p>
              <label className="label block" htmlFor="wiz-tz">{t("setup.you.label")}</label>
              <TimezonePicker id="wiz-tz" value={displayTz} onChange={setDisplayTz} />
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-3">
              <p className="text-sm text-parchment-muted">
                {t("setup.broker.desc")}
              </p>
              <label className="label block" htmlFor="wiz-broker-tz">{t("setup.broker.label")}</label>
              <TimezonePicker id="wiz-broker-tz" value={brokerTz} onChange={setBrokerTz} />
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-4">
              <p className="text-sm text-parchment-muted">
                {t("setup.target.desc")}
              </p>
              <div className="grid gap-2">
                <label className="label" htmlFor="wiz-start">{t("setup.startLabel")}</label>
                <input
                  id="wiz-start"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  className="input"
                  placeholder={t("setup.startPlaceholder")}
                  value={startBalance}
                  onChange={(e) => setStartBalance(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <label className="label" htmlFor="wiz-goal">{t("setup.goalLabel")}</label>
                <input
                  id="wiz-goal"
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="any"
                  className="input"
                  placeholder={t("setup.goalPlaceholder")}
                  value={goalAmount}
                  onChange={(e) => setGoalAmount(e.target.value)}
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-3">
              <p className="text-sm text-parchment-muted">
                {t("setup.levels.desc")}
              </p>
              <label className="label" htmlFor="wiz-pct">{t("setup.pctLabel")}</label>
              <div className="flex items-center gap-2">
                <input
                  id="wiz-pct"
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  max="100"
                  step="any"
                  className="input"
                  value={levelPct}
                  onChange={(e) => setLevelPct(e.target.value)}
                />
                <span className="text-parchment-muted">%</span>
              </div>
            </div>
          )}

          {error && <p className="mt-4 text-sm text-red-400">{error}</p>}

          <div className="mt-7 flex items-center justify-between gap-3">
            <button
              type="button"
              className="btn-ghost inline-flex items-center gap-1.5 disabled:opacity-40"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0 || submitting}
            >
              <ArrowLeft size={16} /> {t("common.back")}
            </button>

            {isLast ? (
              <button
                type="button"
                className="btn-gold inline-flex items-center gap-1.5 disabled:opacity-50"
                onClick={finish}
                disabled={!canNext || submitting}
              >
                {submitting ? t("common.saving") : (<>{t("setup.finish")} <Check size={16} /></>)}
              </button>
            ) : (
              <button
                type="button"
                className="btn-gold inline-flex items-center gap-1.5 disabled:opacity-50"
                onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                disabled={!canNext}
              >
                {t("setup.next")} <ArrowRight size={16} />
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
